import React, { useCallback, useMemo } from 'react';
import { message } from 'antd';
import clsx from 'clsx';
import _ from 'lodash';
import { useSortable } from '@dnd-kit/sortable';

import { useStoreDetails } from 'api/store';
import { ProductCategory } from 'api/types/product';
import { Typography, CardHeader, CardContent, CardActions } from 'ui-kit';
import { ProductCardBase } from 'components/ProductCard';
import ProductCardAdminMenu from './ProductCardAdminMenu';
import ProductCartLabel from '../ProductCartLabel';
import ActiveProduct from './ActiveProduct';
import copyToClipboard from 'copy-to-clipboard';
import { isLocalProduct, ProductLocalStatus } from 'utils/product';
import { getCategoryForProductCard, getOutOfStock } from '../utils';
import { currencyToSymbol, formatCurrency } from 'utils';
import { getStoreLink } from 'utils/utilsV2';

import './ProductCardAdmin.scss';
import { EPriceType } from 'components/NewProductStepper/ProductBaseForm/constants';

interface Props {
  className?: string;
  title: string;
  description?: string;
  amount: number;
  currency: string;
  isPreorder?: boolean;
  isRequest?: boolean;
  isLink?: boolean;
  ownerCommission?: string;
  price: number;
  imgSrc?: string;
  category?: ProductCategory;
  subcategory?: string;
  onEdit: () => void;
  onDelete: () => void;
  onPreview: () => void;
  onPropagate?: () => void;
  onMassDelete?: () => void;
  isActive: boolean;
  itemId: number;
  productStatus: ProductLocalStatus;
  disabled?: boolean;
  hasQuantity: boolean;
  storeId: number;
  productGroup?: number;
  updateStoreProducts: () => void;
  enableDragAndDrop: boolean;
  priceType: EPriceType;
}

const LOCAL_PRODUCT_STATUS_TEXT: Record<ProductLocalStatus, string> = {
  new: 'New local vendor item added!',
  updated: 'New vendor updates to this item!',
};

const ProductCardAdmin = React.forwardRef<HTMLDivElement, Props>(
  ({
    className,
    title,
    description,
    amount,
    currency,
    price,
    onEdit,
    onDelete,
    onPreview,
    onPropagate,
    onMassDelete,
    isPreorder,
    category,
    hasQuantity,
    subcategory,
    isActive,
    itemId,
    productStatus,
    isRequest,
    isLink,
    ownerCommission,
    storeId,
    updateStoreProducts,
    enableDragAndDrop,
    priceType,
    ...rest
  }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
      id: enableDragAndDrop ? itemId : null,
    });

    const outOfStock = getOutOfStock(hasQuantity, amount);
    const { data: storeDetails } = useStoreDetails(storeId);

    const onShare = () => {
      const { isEnabled, subdomain, isSubdomainEnabled } = storeDetails.enterpriseInfo ?? {};
      const storeUrl = getStoreLink({ isEnabled, subdomain, isSubdomainEnabled, storeId });
      const productUrl = storeUrl + '?product=' + itemId;
      copyToClipboard(productUrl);
      message.success('Product link copied to clipboard!');
    };

    const handleCardClick = useCallback(() => {
      if (!enableDragAndDrop) {
        onPreview();
      }
    }, [onPreview, enableDragAndDrop]);

    const movingStyles = useMemo(() => {
      if (enableDragAndDrop && !_.isNil(transform)) {
        return {
          transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
          transition: 'transform 1ms ease',
          opacity: isDragging ? '0.3' : 1,
        };
      }

      return {};
    }, [transform, enableDragAndDrop]);

    return (
      <div ref={setNodeRef} {...attributes} {...listeners} style={movingStyles}>
        <ProductCardBase
          className={clsx(
            className,
            'ProductCardAdmin',
            !isActive && `ProductCardAdmin-inactive`,
            productStatus && `ProductCardAdmin-${productStatus}`,
          )}
          onClick={handleCardClick}
          {...rest}
        >
          <>
            <div className="ProductCardAdmin-float-actions">
              {isPreorder && !isRequest && <ProductCartLabel label="Pre-order" />}
              {isRequest && <ProductCartLabel label="Request" />}
              {isLink && <ProductCartLabel label="Link" />}
            </div>
            {!enableDragAndDrop && (
              <ProductCardAdminMenu
                onEdit={onEdit}
                onDelete={onDelete}
                onShare={onShare}
                onPropagate={onPropagate}
                onMassDelete={onMassDelete}
                category={category}
                isLink={isLink}
                productGroup={rest.productGroup}
              />
            )}
            {outOfStock && (
              <ProductCartLabel label="Out of stock" className="ProductCard_label-outof-stock" />
            )}
            <CardHeader
              title={title}
              subheader={getCategoryForProductCard(category, subcategory, description)}
            />
            <CardContent>
              <div className="ProductCardAdmin-content__details">
                {priceType === EPriceType.VARIABLE ? (
                  <Typography variant="body1" className="ProductCardAdmin-content__details-price">
                    Variable price
                  </Typography>
                ) : (
                  <Typography variant="body1" className="ProductCardAdmin-content__details-price">
                    {!isLink &&
                      `${priceType === EPriceType.MULTIPLE ? 'from' : ''} ${
                        currencyToSymbol(currency) || currency
                      } ${formatCurrency(price)}`}
                    {isLink &&
                      isLocalProduct(category) &&
                      `${parseFloat(ownerCommission) * 100}% of sales`}
                  </Typography>
                )}
                {hasQuantity && !outOfStock && (
                  <Typography
                    variant="body1"
                    color="textGray"
                    className="ProductCardAdmin-content__details-qty"
                  >
                    {amount > 1 ? `${amount} items` : `${amount} item`}
                  </Typography>
                )}
              </div>
            </CardContent>
          </>
          <CardActions>
            {!enableDragAndDrop && (
              <ActiveProduct
                isActive={isActive}
                productId={itemId}
                onUpdate={updateStoreProducts}
              />
            )}
          </CardActions>
        </ProductCardBase>
        {productStatus && (
          <Typography variant="body2" className="ProductCardAdmin-container__footer-status">
            {LOCAL_PRODUCT_STATUS_TEXT[productStatus]}
          </Typography>
        )}
      </div>
    );
  },
);

export default ProductCardAdmin;
